import React from 'react';
import { CheckCircle, Eye, Clock } from 'lucide-react';

const NotificationFeed = ({
  notifications,
  loading,
  selectedNotifications,
  onSelectNotification,
  onSelectAll,
  onAcknowledge,
  onViewDetails,
  getPriorityColor,
  getPriorityIcon
}) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'audit':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'compliance':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'environmental':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (notifications?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">All Clear!</h3>
        <p className="text-gray-600">No notifications match your current filters.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header with Bulk Actions */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedNotifications?.length === notifications?.length}
              onChange={onSelectAll}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              {selectedNotifications?.length > 0 
                ? `${selectedNotifications?.length} selected` 
                : 'Select all'}
            </span>
          </label>

          <div className="text-sm text-gray-600">
            {notifications?.length} notification{notifications?.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Notification List */}
      <div className="divide-y divide-gray-200">
        {notifications?.map((notification) => (
          <div
            key={notification?.id}
            className={`p-4 hover:bg-gray-50 transition-colors ${
              notification?.isAcknowledged ? 'opacity-60' : ''
            }`}
          >
            <div className="flex items-start gap-4">
              {/* Checkbox */}
              <div className="flex items-center pt-1">
                <input
                  type="checkbox"
                  checked={selectedNotifications?.includes(notification?.id)}
                  onChange={() => onSelectNotification(notification?.id)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>

              {/* Priority Icon */}
              <div className={`flex-shrink-0 ${getPriorityColor(notification?.priority)}`}>
                {getPriorityIcon(notification?.priority)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${
                        getTypeColor(notification?.type)
                      }`}>
                        {notification?.type?.replace(/_/g, ' ')?.toUpperCase()}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        notification?.priority === 'critical' ? 'bg-red-100 text-red-800 border border-red-200' :
                        notification?.priority === 'high' ? 'bg-orange-100 text-orange-800 border border-orange-200' :
                        notification?.priority === 'medium'? 'bg-yellow-100 text-yellow-800 border border-yellow-200' : 'bg-blue-100 text-blue-800 border border-blue-200'
                      }`}>
                        {notification?.priority?.toUpperCase()}
                      </span>
                    </div>
                    
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                      {notification?.title || notification?.message?.substring(0, 80)}
                    </h4>
                    
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {notification?.message}
                    </p>

                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(notification?.created_at)}
                      </div>
                      
                      {notification?.isAcknowledged && (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-3 h-3" />
                          Acknowledged
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => onViewDetails(notification)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>

                    {!notification?.isAcknowledged && notification?.type !== 'audit' && (
                      <button
                        onClick={() => onAcknowledge(notification)}
                        className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Acknowledge
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationFeed;